import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { saveConflictCheckResults, searchConflictCheck } from "api";
import { Label } from "@progress/kendo-react-labels";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Tile } from "components/UIComponents/Tile";
import { Loader } from "@progress/kendo-react-indicators"; // Import your loader component
import { toast } from "react-toastify";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { FaSearch } from "react-icons/fa";
import { RedirectCell } from "components/common/GridComponents";
import styles from "./Casefile.module.scss";

const ConflictCheck = ({
  fromCaseFile,
  onClose,
  onSaved,
  moveNext,
  formData,
  matterId,
  currentMatterDetails,
  setHandlefn,
}) => {
  const DEFAULT_STATE = {
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    postcode: "",
    companyName: "",
    companyRegNo: "",
    matterDetails: "",
  };

  const [loading, setLoading] = useState(false); // State for loading
  const [data, setData] = useState(null);
  const [inputState, setInputState] = useState(DEFAULT_STATE);

  const matter_Id = fromCaseFile ? matterId : formData?.matterDetails?.matterId;

  const matter_details = fromCaseFile
    ? currentMatterDetails
    : formData?.matterDetails;

  const handleInputChange = (value, label) => {
    setInputState((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await searchConflictCheck(matter_Id, inputState);
      setData(response?.data?.data);
      console.log(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const saveAndSearch = async (startNew) => {
    try {
      setLoading(true);
      await saveConflictCheckResults(matter_Id, inputState);
      onSaved?.();
      if (startNew) {
        setData([]);
        setInputState(DEFAULT_STATE);
      } else {
        onClose();
      }
      toast.success("Search Saved");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleNext = useCallback(async () => {
    try {
      if (data) await saveConflictCheckResults(matter_Id, inputState);
    } catch (error) {
      console.error("Failed to save conflict check results", error);
    } finally {
      moveNext({}, { conflictCheckData: data });
    }
  }, [moveNext, data, matter_Id, inputState]);

  const clientSubType =
    formData?.clientData?.clientSubType || currentMatterDetails?.client?.type;
  const isPerson =
    clientSubType?.toLowerCase() === "individual" ||
    clientSubType?.toLowerCase() === "person";
  const isCompany = clientSubType?.toLowerCase() === "company";

  // Enable Search only if at least one visible field has a value
  const hasSearchableValue = useMemo(() => {
    if (isPerson) {
      return (
        inputState.firstName || inputState.lastName || inputState.dateOfBirth
      );
    }
    if (isCompany) {
      return (
        inputState.companyName || inputState.companyRegNo || inputState.postCode
      );
    }
    return false;
  }, [isPerson, isCompany, inputState]);

  useEffect(() => {
    if (setHandlefn) {
      setHandlefn(() => handleNext);
      return () => setHandlefn && setHandlefn(null);
    }
  }, [setHandlefn, handleNext]);

  const content = (
    <div>
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
          }}
        >
          <Loader themeColor="warning" size="large" type="pulsing" />
        </div>
      )}
      <div
        className={`${styles["sub-text"]} m-3 ms-2 mt-1 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto`}
      >
        <i>
          {matter_details?.client?.clientName} - {matter_details?.matterRef} -{" "}
          {matter_details?.details}{" "}
          {matter_details?.appCode && (
            <span> - [{matter_details?.appCode}]</span>
          )}{" "}
        </i>
      </div>
      <div
        className={`${
          setHandlefn && "bg-white m-3 shadow rounded-3 px-2 py-3"
        }`}
      >
        <Tile
          height={"210px"}
          heading="Search"
          headingClass=""
          className={`${
            setHandlefn ? "bg-transparent shadow-none" : "m-3"
          }  mt-1 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto`}
        >
          <div className={`${setHandlefn ? styles["item-container"] : "row"}`}>
            {isPerson && (
              <>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">First Name</Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="First Name"
                    onChange={(e) => handleInputChange(e.value, "firstName")}
                    value={inputState["firstName"]}
                  />
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">Last Name</Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Last Name"
                    onChange={(e) => handleInputChange(e.value, "lastName")}
                    value={inputState["lastName"]}
                  />
                </div>
                <div
                  className={`${
                    setHandlefn ? "d-flex flex-column" : "col-4"
                  } form-date-picker note-date-picker check-date-picker`}
                >
                  <Label className="label d-block mb-1">Date Of Birth</Label>
                  <DatePicker
                    style={{ width: "260px" }}
                    placeholder="Select Date"
                    toggleButton={CalenderCustomToggleButton}
                    onChange={(e) => handleInputChange(e.value, "dateOfBirth")}
                    value={inputState["dateOfBirth"]}
                  />
                </div>
              </>
            )}

            {isCompany && (
              <>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">Company Name</Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Company Name"
                    onChange={(e) => handleInputChange(e.value, "companyName")}
                    value={inputState["companyName"]}
                  />
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">
                    Company Registration No
                  </Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Reg No"
                    onChange={(e) => handleInputChange(e.value, "companyRegNo")}
                    value={inputState["companyRegNo"]}
                  />
                </div>
                <div
                  className={`${setHandlefn ? "d-flex flex-column" : "col-4"}`}
                >
                  <Label className="label d-block mb-1">Post Code</Label>
                  <Input
                    style={{ width: "260px", height: "32px" }}
                    placeholder="Post Code"
                    onChange={(e) => handleInputChange(e.value, "postCode")}
                    value={inputState["postCode"]}
                  />
                </div>
              </>
            )}
          </div>
          <div
            className={`${
              setHandlefn ? "d-flex flex-column gap-2 flex-xl-row" : "row"
            } mt-3`}
          >
            <div
              className={
                setHandlefn ? styles["matter-details-container"] : "col-8"
              }
            >
              <Label className="label d-block mb-1">Matter Details</Label>
              <Input
                id="payer"
                style={{
                  maxWidth: "100%",
                  height: "32px",
                }}
                placeholder="Matter Details"
                onChange={(e) => handleInputChange(e.value, "matterDetails")}
                value={inputState["matterDetails"]}
              ></Input>
            </div>
            <div
              className={`
                ${
                  setHandlefn ? "col-12 col-xl-4 " : "col-4"
                } d-flex justify-content-end mt-3`}
            >
              <Button
                className="common-btn"
                onClick={handleSearch}
                disabled={!hasSearchableValue || loading}
              >
                <div className="d-flex gap-2 align-items-center">
                  <FaSearch color="white" />
                  <div>Search</div>
                </div>
              </Button>
            </div>
          </div>
        </Tile>
        {data && (
          <Tile
            heading="Search Results"
            headingClass=""
            className={`${
              setHandlefn ? "bg-transparent shadow-none" : "m-3"
            }  mt-1 p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto`}
          >
            <Grid data={data} style={{ height: "200px" }} className="pb-1">
              <GridColumn
                field="matched"
                title="Matched"
                className="ps-4 fs-sm"
                headerClassName="ps-4"
                width={30}
                editor="text"
              />
              <GridColumn
                field="details"
                title="Details"
                width={200}
                className="fs-sm"
                editable={false}
              />
              {/* <GridColumn width="60px" cell={RedirectCell} /> */}
            </Grid>
          </Tile>
        )}
      </div>
      {fromCaseFile && (
        <div className="dialog-footer">
          <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
            Close
          </Button>

          <Button
            className="common-btn me-2"
            onClick={() => saveAndSearch(true)}
          >
            Save & New Search
          </Button>

          <Button
            className="common-btn  me-3"
            onClick={() => saveAndSearch(false)}
          >
            Save & Close
          </Button>
        </div>
      )}
    </div>
  );

  return formData ? (
    <Tile
      heading="Conflict Check"
      className="px-4 h-100 overflow-auto bg-transparent border-0 border w-100 bg-danger shadow-none "
    >
      <div style={{ height: "calc(100% - 110px)" }}>
        <div className="overflow-auto" style={{ height: "calc(100% - 55px)" }}>
          <div className={`${!setHandlefn && styles["onboarding_container"]}`}>
            {content}
          </div>
        </div>
        <div
          className={`${
            !setHandlefn && styles["onboarding_container"]
          } d-flex flex-column`}
          style={{ height: "55px" }}
        >
          <div className="d-flex justify-content-end mt-2">
            <Button
              className="common-btn bgBtnSecondary me-2"
              onClick={() => saveAndSearch(true)}
            >
              Save & New Search
            </Button>
          </div>
          <hr className="my-2 w-100" />
        </div>
      </div>
    </Tile>
  ) : (
    <div>{content}</div>
  );
};

export default ConflictCheck;
